import React, { useState, useEffect, useMemo } from 'react';
import { FaFileCsv, FaFilePdf, FaUsers, FaUserTie, FaHotel, FaUserFriends, FaBuilding, FaFileContract, FaCreditCard, FaShieldAlt, FaFlag, FaArrowLeft, FaFilter, FaList } from 'react-icons/fa';
import {
  getAllUsers,
  getAdminProperties,
  getAdminTenancies,
  getAdminSubscriptions,
  getAuditLogs,
  getReportData
} from '../../../api/services/adminService';
import { getAdminReports } from '../../../api/services/reportService';
import { exportToCsv, exportToPdf } from '../../../utils/exportUtils';
import { EmptyState } from './AdminUI';
import './DataExports.css';

const FETCH_PAGE_SIZE = 100;
const MAX_PAGES = 100; // hard safety cap (10k rows)

/** Pull every page of a Spring `Page` admin endpoint into a single flat array. */
async function fetchAllPages(fetchPage) {
  const all = [];
  let page = 0;
  let totalPages = 1;
  do {
    const res = await fetchPage(page);
    const inner = res?.data ?? res;
    const content = Array.isArray(inner?.content) ? inner.content : Array.isArray(inner) ? inner : [];
    all.push(...content);
    totalPages = inner?.totalPages ?? 1;
    page += 1;
  } while (page < totalPages && page < MAX_PAGES);
  return all;
}

const fullName = (u) => `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || '—';
const roleList = (u) => (Array.isArray(u?.roles) ? u.roles.join(', ') : '');
const hasRole = (u, role) =>
  Array.isArray(u?.roles) && u.roles.some((r) => String(r).toUpperCase() === role);

// ---- Report definitions: each knows how to fetch its rows and which columns to export. ----
const REPORTS = [
  {
    id: 'users',
    title: 'All Users',
    description: 'Every registered user with contact, role, status and verification.',
    icon: <FaUsers />,
    accent: 'blue',
    fetch: (filters) => getReportData('users', { ...filters }),
    columns: [
      { header: 'Name', accessor: fullName },
      { header: 'Email', accessor: 'email' },
      { header: 'Phone', accessor: 'phone' },
      { header: 'ID Number', accessor: 'idNumber' },
      { header: 'Roles', accessor: roleList },
      { header: 'Active', accessor: 'isActive' },
      { header: 'KYC', accessor: 'kycStatus' },
      { header: 'KYB', accessor: 'kybStatus' },
      { header: 'Joined', accessor: 'createdAt' },
    ],
  },
  {
    id: 'landlords',
    title: 'Landlords',
    description: 'Users holding the LANDLORD role.',
    icon: <FaUserTie />,
    accent: 'indigo',
    fetch: (filters) => getReportData('users', { ...filters, role: 'LANDLORD' }),
    columns: [
      { header: 'Name', accessor: fullName },
      { header: 'Email', accessor: 'email' },
      { header: 'Phone', accessor: 'phone' },
      { header: 'KYC', accessor: 'kycStatus' },
      { header: 'KYB', accessor: 'kybStatus' },
      { header: 'Active', accessor: 'isActive' },
      { header: 'Joined', accessor: 'createdAt' },
    ],
  },
  {
    id: 'bnb-hosts',
    title: 'Airbnb Hosts',
    description: 'Users holding the BNB_HOST role.',
    icon: <FaHotel />,
    accent: 'teal',
    fetch: (filters) => getReportData('users', { ...filters, role: 'BNB_HOST' }),
    columns: [
      { header: 'Name', accessor: fullName },
      { header: 'Email', accessor: 'email' },
      { header: 'Phone', accessor: 'phone' },
      { header: 'KYC', accessor: 'kycStatus' },
      { header: 'Active', accessor: 'isActive' },
      { header: 'Joined', accessor: 'createdAt' },
    ],
  },
  {
    id: 'agents',
    title: 'Agents',
    description: 'Users holding the AGENT role.',
    icon: <FaUserFriends />,
    accent: 'purple',
    fetch: (filters) => getReportData('users', { ...filters, role: 'AGENT' }),
    columns: [
      { header: 'Name', accessor: fullName },
      { header: 'Email', accessor: 'email' },
      { header: 'Phone', accessor: 'phone' },
      { header: 'Active', accessor: 'isActive' },
      { header: 'Joined', accessor: 'createdAt' },
    ],
  },
  {
    id: 'tenants',
    title: 'Tenants',
    description: 'Users holding the TENANT role, with tenant code.',
    icon: <FaUsers />,
    accent: 'blue',
    fetch: (filters) => getReportData('users', { ...filters, role: 'TENANT' }),
    columns: [
      { header: 'Name', accessor: fullName },
      { header: 'Email', accessor: 'email' },
      { header: 'Phone', accessor: 'phone' },
      { header: 'Tenant Code', accessor: 'tenantCode' },
      { header: 'KYC', accessor: 'kycStatus' },
      { header: 'Joined', accessor: 'createdAt' },
    ],
  },
  {
    id: 'properties',
    title: 'Properties',
    description: 'All properties with owner contact, units and status.',
    icon: <FaBuilding />,
    accent: 'indigo',
    fetch: (filters) => getReportData('properties', filters),
    columns: [
      { header: 'Name', accessor: 'name' },
      { header: 'Address', accessor: 'address' },
      { header: 'City', accessor: (p) => p?.location?.city },
      { header: 'Owner Email', accessor: (p) => p?.contacts?.email },
      { header: 'Total Units', accessor: (p) => p?.totalUnits ?? p?.unitCount },
      { header: 'Occupied', accessor: 'occupiedUnits' },
      { header: 'Vacant', accessor: 'vacantUnits' },
      { header: 'Active', accessor: 'isActive' },
      { header: 'Published', accessor: 'published' },
      { header: 'Created', accessor: 'createdAt' },
    ],
  },
  {
    id: 'tenancies',
    title: 'Property Tenancies',
    description: 'Tenant assignments across all properties (rentals & BnB stays).',
    icon: <FaFileContract />,
    accent: 'teal',
    fetch: (filters) => getReportData('tenancies', filters),
    columns: [
      { header: 'Tenant', accessor: (t) => `${t?.userFirstName || ''} ${t?.userLastName || ''}`.trim() },
      { header: 'Tenant Email', accessor: 'userEmail' },
      { header: 'Tenant Code', accessor: 'tenantCode' },
      { header: 'Property', accessor: 'propertyName' },
      { header: 'Unit', accessor: (t) => t?.unitNumber || t?.unitName },
      { header: 'Type', accessor: (t) => (t?.isBnbStay ? 'BnB' : 'Rental') },
      { header: 'Status', accessor: 'status' },
      { header: 'Monthly Rent', accessor: 'monthlyRent' },
      { header: 'Deposit', accessor: 'depositAmount' },
      { header: 'Start', accessor: 'startDate' },
      { header: 'End', accessor: 'endDate' },
    ],
  },
  {
    id: 'subscriptions',
    title: 'Subscriptions',
    description: 'All user subscriptions and their billing state.',
    icon: <FaCreditCard />,
    accent: 'purple',
    fetch: (filters) => getReportData('subscriptions', filters),
    columns: [
      { header: 'User', accessor: (s) => s?.userEmail || s?.userName || s?.userId },
      { header: 'Plan', accessor: (s) => s?.planTitle || s?.planName || s?.planId },
      { header: 'Status', accessor: 'status' },
      { header: 'Amount', accessor: (s) => s?.amount ?? s?.planAmount },
      { header: 'Start', accessor: (s) => s?.startDate || s?.startsAt },
      { header: 'End', accessor: (s) => s?.endDate || s?.endsAt },
      { header: 'Created', accessor: 'createdAt' },
    ],
  },
  {
    id: 'reports',
    title: 'Complaints / Reports',
    description: 'User-submitted reports and complaints (all statuses).',
    icon: <FaFlag />,
    accent: 'red',
    fetch: async () => {
      const statuses = ['OPEN', 'RESOLVED', 'CLOSED'];
      const all = [];
      for (const status of statuses) {
        const rows = await fetchAllPages((page) => getAdminReports({ level: 'ADMIN', status, page, size: FETCH_PAGE_SIZE }));
        all.push(...rows);
      }
      return all;
    },
    columns: [
      { header: 'Title', accessor: 'title' },
      { header: 'Level', accessor: 'level' },
      { header: 'Category', accessor: 'category' },
      { header: 'Status', accessor: 'status' },
      { header: 'Reporter', accessor: (r) => r?.reporterEmail || r?.reporterName || r?.reporterUserId },
      { header: 'Target', accessor: (r) => r?.targetEmail || r?.targetUserId },
      { header: 'Created', accessor: 'createdAt' },
    ],
  },
  {
    id: 'audit',
    title: 'Audit Logs',
    description: 'Admin action history for compliance and review.',
    icon: <FaShieldAlt />,
    accent: 'slate',
    fetch: () => fetchAllPages((page) => getAuditLogs({ page, size: FETCH_PAGE_SIZE })),
    columns: [
      { header: 'Action', accessor: 'action' },
      { header: 'Admin', accessor: (a) => a?.adminEmail || a?.adminUserId },
      { header: 'Target', accessor: (a) => a?.targetEmail || a?.targetUserId },
      { header: 'Description', accessor: 'description' },
      { header: 'IP', accessor: 'ipAddress' },
      { header: 'When', accessor: 'createdAt' },
    ],
  },
];

const DataExports = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [busy, setBusy] = useState(null); // `${id}:${format}`
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [rowLimit, setRowLimit] = useState('20');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [kycFilter, setKycFilter] = useState('ALL');
  const [cityFilter, setCityFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('ALL');
  const [stayTypeFilter, setStayTypeFilter] = useState('ALL');
  const [complaintFilter, setComplaintFilter] = useState('ALL');
  const [complaintCategory, setComplaintCategory] = useState('ALL');

  // Revenue filters (Expected rent for properties, monthly rent for tenancies, amount for subscriptions)
  const [minRevenue, setMinRevenue] = useState('');
  const [maxRevenue, setMaxRevenue] = useState('');

  const buildFilters = () => ({
    search: searchQuery,
    role: roleFilter,
    status: statusFilter,
    kyc: kycFilter,
    city: cityFilter,
    visibility: visibilityFilter,
    type: stayTypeFilter,
    complaintStatus: complaintFilter,
    category: complaintCategory,
    minRevenue,
    maxRevenue,
    size: rowLimit === 'ALL' ? 10000 : parseInt(rowLimit, 10),
    page: 0
  });

  const handleApplyFilters = async () => {
    if (!selectedReport) return;
    setLoadingPreview(true);
    setError(null);
    try {
      const data = await selectedReport.fetch(buildFilters());
      setRawData(data?.content || data || []);
    } catch (err) {
      setError(err?.message || `Failed to fetch data for ${selectedReport.title}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Load report data on selection (resetting filters)
  const handleSelectReport = async (report) => {
    setSelectedReport(report);
    setRawData([]);
    setError(null);
    setNotice(null);
    
    // Reset filters
    setSearchQuery('');
    setRowLimit('20');
    setRoleFilter('ALL');
    setStatusFilter('ALL');
    setKycFilter('ALL');
    setCityFilter('');
    setVisibilityFilter('ALL');
    setStayTypeFilter('ALL');
    setComplaintFilter('ALL');
    setComplaintCategory('ALL');
    setMinRevenue('');
    setMaxRevenue('');

    setLoadingPreview(true);
    try {
      const data = await report.fetch({ size: 20, page: 0 });
      setRawData(data?.content || data || []);
    } catch (err) {
      setError(err?.message || `Failed to fetch data for ${report.title}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Auto-select first report on mount
  useEffect(() => {
    if (REPORTS.length > 0 && !selectedReport) {
      handleSelectReport(REPORTS[0]);
    }
  }, []);

  const runExport = async (format) => {
    if (!selectedReport) return;
    setError(null);
    setNotice(null);
    setBusy(`${selectedReport.id}:${format}`);
    try {
      if (rawData.length === 0) {
        setNotice(`No records to export after applying filters.`);
        return;
      }
      const baseName = `tenantscore-${selectedReport.id}`;
      if (format === 'csv') {
        exportToCsv(rawData, selectedReport.columns, baseName);
      } else {
        exportToPdf(rawData, selectedReport.columns, baseName, { title: `${selectedReport.title} Report`, subtitle: 'TenantScore' });
      }
      setNotice(`Exported ${rawData.length} record${rawData.length === 1 ? '' : 's'} from "${selectedReport.title}" as ${format.toUpperCase()}.`);
    } catch (e) {
      setError(e?.message || `Failed to export ${selectedReport.title}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="exports-container">
      <div className="exports-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ flex: 1, minWidth: '280px' }}>
            <h1 className="page-title" style={{ margin: 0 }}>Data Exports &amp; Reports</h1>
            <p className="exports-subtitle" style={{ margin: '4px 0 0 0' }}>
              Select a dataset category from the dropdown to preview actual system records, apply custom filters, and export.
            </p>
          </div>
          
          <div className="dataset-selector-group">
            <label className="selector-label">Select Dataset Category:</label>
            <select
              className="dataset-select"
              value={selectedReport?.id || ''}
              onChange={(e) => {
                const report = REPORTS.find(r => r.id === e.target.value);
                if (report) handleSelectReport(report);
              }}
            >
              {REPORTS.map((report) => (
                <option key={report.id} value={report.id}>
                  {report.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error ? <div className="exports-alert error" role="alert">{error}</div> : null}
      {notice ? <div className="exports-alert success" role="status">{notice}</div> : null}

      <div className="exports-layout">
        <main className="exports-main">
          {selectedReport ? (
            <>
              <div className="preview-header-section">
                <div className={`preview-icon ${selectedReport.accent}`}>{selectedReport.icon}</div>
                <div className="preview-header-text">
                  <h2 className="preview-title">{selectedReport.title}</h2>
                  <p className="preview-desc">{selectedReport.description}</p>
                </div>
              </div>

              {/* Filter Panel */}
              <div className="filter-panel">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#02236A', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaFilter style={{ fontSize: 11 }} /> Filter &amp; Customize Export
                  </h3>
                  <button onClick={handleApplyFilters} className="export-btn" style={{ padding: '6px 12px', fontSize: 13, background: '#02236A', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                    Apply Filters
                  </button>
                </div>
                
                <div className="filter-row">
                  <div className="filter-item">
                    <label className="filter-label">Search Preview</label>
                    <input
                      type="text"
                      className="filter-input"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Type to filter..."
                    />
                  </div>

                  <div className="filter-item">
                    <label className="filter-label">Row Limit</label>
                    <select
                      className="filter-select"
                      value={rowLimit}
                      onChange={(e) => setRowLimit(e.target.value)}
                    >
                      <option value="10">First 10 rows</option>
                      <option value="20">First 20 rows</option>
                      <option value="50">First 50 rows</option>
                      <option value="100">First 100 rows</option>
                      <option value="500">First 500 rows</option>
                      <option value="ALL">Export All rows</option>
                    </select>
                  </div>

                  {/* Custom filters based on report ID */}
                  {selectedReport.id === 'users' && (
                    <>
                      <div className="filter-item">
                        <label className="filter-label">Role</label>
                        <select className="filter-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                          <option value="ALL">All Roles</option>
                          <option value="LANDLORD">Landlord</option>
                          <option value="BNB_HOST">Airbnb Host</option>
                          <option value="AGENT">Agent</option>
                          <option value="TENANT">Tenant</option>
                          <option value="ADMIN">Admin</option>
                        </select>
                      </div>
                      <div className="filter-item">
                        <label className="filter-label">User Status</label>
                        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                          <option value="ALL">All Statuses</option>
                          <option value="ACTIVE">Active Only</option>
                          <option value="BLOCKED">Blocked Only</option>
                        </select>
                      </div>
                      <div className="filter-item">
                        <label className="filter-label">KYC Status</label>
                        <select className="filter-select" value={kycFilter} onChange={(e) => setKycFilter(e.target.value)}>
                          <option value="ALL">All KYC Statuses</option>
                          <option value="PENDING">Pending</option>
                          <option value="APPROVED">Approved</option>
                          <option value="REJECTED">Rejected</option>
                        </select>
                      </div>
                    </>
                  )}

                  {['landlords', 'bnb-hosts', 'agents', 'tenants'].includes(selectedReport.id) && (
                    <>
                      <div className="filter-item">
                        <label className="filter-label">User Status</label>
                        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                          <option value="ALL">All Statuses</option>
                          <option value="ACTIVE">Active Only</option>
                          <option value="BLOCKED">Blocked Only</option>
                        </select>
                      </div>
                      {selectedReport.id !== 'agents' && (
                        <div className="filter-item">
                          <label className="filter-label">KYC Status</label>
                          <select className="filter-select" value={kycFilter} onChange={(e) => setKycFilter(e.target.value)}>
                            <option value="ALL">All KYC Statuses</option>
                            <option value="PENDING">Pending</option>
                            <option value="APPROVED">Approved</option>
                            <option value="REJECTED">Rejected</option>
                          </select>
                        </div>
                      )}
                    </>
                  )}

                  {selectedReport.id === 'properties' && (
                    <>
                      <div className="filter-item">
                        <label className="filter-label">City</label>
                        <input
                          type="text"
                          className="filter-input"
                          value={cityFilter}
                          onChange={(e) => setCityFilter(e.target.value)}
                          placeholder="Filter by city..."
                        />
                      </div>
                      <div className="filter-item">
                        <label className="filter-label">Property Status</label>
                        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                          <option value="ALL">All Statuses</option>
                          <option value="ACTIVE">Active Only</option>
                          <option value="BLOCKED">Blocked Only</option>
                        </select>
                      </div>
                      <div className="filter-item">
                        <label className="filter-label">Visibility (Published)</label>
                        <select className="filter-select" value={visibilityFilter} onChange={(e) => setVisibilityFilter(e.target.value)}>
                          <option value="ALL">All</option>
                          <option value="PUBLISHED">Published Only</option>
                          <option value="UNPUBLISHED">Unpublished Only</option>
                        </select>
                      </div>
                      <div className="filter-item">
                        <label className="filter-label">Min Expected Revenue</label>
                        <input
                          type="number"
                          className="filter-input"
                          value={minRevenue}
                          onChange={(e) => setMinRevenue(e.target.value)}
                          placeholder="Min expected rent..."
                        />
                      </div>
                      <div className="filter-item">
                        <label className="filter-label">Max Expected Revenue</label>
                        <input
                          type="number"
                          className="filter-input"
                          value={maxRevenue}
                          onChange={(e) => setMaxRevenue(e.target.value)}
                          placeholder="Max expected rent..."
                        />
                      </div>
                    </>
                  )}

                  {selectedReport.id === 'tenancies' && (
                    <>
                      <div className="filter-item">
                        <label className="filter-label">Stay Type</label>
                        <select className="filter-select" value={stayTypeFilter} onChange={(e) => setStayTypeFilter(e.target.value)}>
                          <option value="ALL">All Types</option>
                          <option value="RENTAL">Long-term Rental</option>
                          <option value="BNB">Airbnb Stay</option>
                        </select>
                      </div>
                      <div className="filter-item">
                        <label className="filter-label">Tenancy Status</label>
                        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                          <option value="ALL">All Statuses</option>
                          <option value="ACTIVE">Active</option>
                          <option value="ENDED">Ended</option>
                          <option value="PENDING">Pending</option>
                        </select>
                      </div>
                      <div className="filter-item">
                        <label className="filter-label">Min Rent (Revenue)</label>
                        <input
                          type="number"
                          className="filter-input"
                          value={minRevenue}
                          onChange={(e) => setMinRevenue(e.target.value)}
                          placeholder="Min monthly rent..."
                        />
                      </div>
                      <div className="filter-item">
                        <label className="filter-label">Max Rent (Revenue)</label>
                        <input
                          type="number"
                          className="filter-input"
                          value={maxRevenue}
                          onChange={(e) => setMaxRevenue(e.target.value)}
                          placeholder="Max monthly rent..."
                        />
                      </div>
                    </>
                  )}

                  {selectedReport.id === 'subscriptions' && (
                    <>
                      <div className="filter-item">
                        <label className="filter-label">Subscription Status</label>
                        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                          <option value="ALL">All Statuses</option>
                          <option value="ACTIVE">Active</option>
                          <option value="EXPIRED">Expired</option>
                          <option value="CANCELLED">Cancelled</option>
                        </select>
                      </div>
                      <div className="filter-item">
                        <label className="filter-label">Min Revenue (Paid)</label>
                        <input
                          type="number"
                          className="filter-input"
                          value={minRevenue}
                          onChange={(e) => setMinRevenue(e.target.value)}
                          placeholder="Min paid amount..."
                        />
                      </div>
                      <div className="filter-item">
                        <label className="filter-label">Max Revenue (Paid)</label>
                        <input
                          type="number"
                          className="filter-input"
                          value={maxRevenue}
                          onChange={(e) => setMaxRevenue(e.target.value)}
                          placeholder="Max paid amount..."
                        />
                      </div>
                    </>
                  )}

                  {selectedReport.id === 'reports' && (
                    <>
                      <div className="filter-item">
                        <label className="filter-label">Complaint Status</label>
                        <select className="filter-select" value={complaintFilter} onChange={(e) => setComplaintFilter(e.target.value)}>
                          <option value="ALL">All Statuses</option>
                          <option value="OPEN">Open</option>
                          <option value="RESOLVED">Resolved</option>
                          <option value="CLOSED">Closed</option>
                        </select>
                      </div>
                      <div className="filter-item">
                        <label className="filter-label">Category</label>
                        <select className="filter-select" value={complaintCategory} onChange={(e) => setComplaintCategory(e.target.value)}>
                          <option value="ALL">All Categories</option>
                          <option value="MAINTENANCE">Maintenance</option>
                          <option value="NOISE">Noise</option>
                          <option value="PAYMENT">Payment</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>

                <div className="preview-actions-row">
                  <div className="preview-meta">
                    Showing {rawData.length} records. To export more data, change the row limit and apply filters.
                  </div>
                  <div className="preview-download-buttons">
                    <button
                      type="button"
                      className="export-btn csv"
                      onClick={() => runExport('csv')}
                      disabled={busy}
                    >
                      <FaFileCsv /> {busy === `${selectedReport.id}:csv` ? 'Preparing…' : 'Export CSV'}
                    </button>
                    <button
                      type="button"
                      className="export-btn pdf"
                      onClick={() => runExport('pdf')}
                      disabled={busy}
                    >
                      <FaFilePdf /> {busy === `${selectedReport.id}:pdf` ? 'Preparing…' : 'Export PDF'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Data Preview Table */}
              <div className="preview-table-card">
                <div className="preview-table-title-bar">
                  <h3 className="preview-table-title" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                    <FaList style={{ fontSize: 12 }} /> Data Preview
                  </h3>
                </div>

                {loadingPreview ? (
                  <div className="preview-loading">
                    <div className="preview-spinner"></div>
                    <div>Fetching data preview...</div>
                  </div>
                ) : rawData.length === 0 ? (
                  <EmptyState
                    icon="🗄️"
                    title="No data matches filters"
                    text="Try adjusting your search query or filters above."
                  />
                ) : (
                  <div className="preview-table-wrap">
                    <table className="preview-table">
                      <thead>
                        <tr>
                          {selectedReport.columns.map((col, i) => (
                            <th key={i}>{col.header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rawData.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {selectedReport.columns.map((col, colIndex) => {
                              const val = typeof col.accessor === 'function' ? col.accessor(row) : row[col.accessor];
                              return (
                                <td key={colIndex}>
                                  {val === true ? 'Yes' : val === false ? 'No' : val === null || val === undefined || val === '' ? '—' : String(val)}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
              Select a dataset from the dropdown menu above to preview data.
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default DataExports;
